/**
 * FedRAMP Prepare — V98-3 review U5 (glance B4/H). The Prepare tab leads
 * with what decides the period: KSI readiness for the program's class, the
 * ONE action that moves it ("Fix N gaps", jumping to the gap list on this
 * page), and the program's overdue items inline — each with its action.
 *
 * Dates are reconciled with the rail and the card: the window line is the
 * period's own window ("opens <start>" while upcoming, "ends <end>" once
 * open), the same one the StageRail meta and the card's next date name.
 *
 * Reads (any member): GET /frameworks/:fw/readiness?class=, GET /blockers
 * (caller-scoped). Writes: none here — each item opens its own page.
 */
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Spinner } from '@aigovops/design-system';
import { getProgramManifest, sourceNameOf } from '@aigovops/core';
import { getFrameworkReadiness, listWorkspaceMembers, type ProgramCard, type Soc2Requirement } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { useOptionalAuth } from '../../lib/auth';
import { listBlockers } from '../../lib/coverage-api';
import { dueChip, itemHref, rowAct } from '../../lib/today-model';
import { fmtProgramDate, readinessSplit } from '../../lib/program-shell';
import { Chip } from '../today/shared';
import { ReadinessHero } from './ReadinessHero';
import { coveringAcceptance, isCouldntRequirement, requirementItems } from '../../lib/couldnt-check';
import { AcceptedLine, AcceptRisk, CouldntCheckChip, CouldntCheckFixes, CouldntCheckWhy, ShowAllReasons } from '../CouldntCheck';
import { useRollupShown } from '../sweep/ProgramRollup';
import '../../pages/today.css';

/** One KSI GAC Pro couldn't check: why, its fix per source, an acceptance that holds (R2-H5), one "show all" toggle. */
function KsiCouldntRow({ r, prefix, ownerName }: { r: Soc2Requirement; prefix: string; ownerName: (id: string) => string | null }) {
  const [all, setAll] = useState(false);
  const items = requirementItems(r);
  const accepted = coveringAcceptance(r.risk_acceptances);
  return (
    <li className="gp-ow" data-check-state="couldnt_check">
      <span className="gp-ow-main"><span className="gp-ow-t">{`${r.ref} · ${r.title}`}</span>
        <span className="gp-ow-meta"><CouldntCheckChip /><CouldntCheckWhy items={items} unit="control" expanded={all} /></span>
        {accepted ? <AcceptedLine acceptance={accepted} ownerName={ownerName(accepted.owner_user_id)} /> : null}
        <ShowAllReasons items={items} expanded={all} onToggle={() => setAll((x) => !x)} /></span>
      <span className="gp-ow-act"><CouldntCheckFixes items={items} prefix={prefix} ctx={{ ref: r.ref }} compact expanded={all} /></span>
    </li>
  );
}

/** The KSIs no open acceptance covers, as the accept-the-risk form lists them (the connection named when there is one). */
function acceptTargets(rows: readonly Soc2Requirement[]) {
  return rows.filter((r) => !coveringAcceptance(r.risk_acceptances)).map((r) => {
    const kinds = [...new Set(requirementItems(r).map((i) => i.kind).filter((k): k is string => !!k))];
    return { label: `${r.ref} ${r.title}`, registerItemId: r.requirement_id, connection: kinds.length === 1 ? sourceNameOf(kinds[0]!) : null };
  });
}

export function FedrampPrepare({ slug, prefix, card, periodId }: { slug: string; prefix: string; card: ProgramCard; periodId: string }) {
  const { role } = useWorkspace();
  const admin = role === 'owner' || role === 'admin';
  const rollupShown = useRollupShown();
  const viewerId = useOptionalAuth()?.user?.id ?? '';
  const qc = useQueryClient();
  const members = useQuery({ queryKey: ['ws-members', slug], queryFn: () => listWorkspaceMembers(slug), staleTime: 60_000, retry: false });
  const ownerName = (id: string) => members.data?.members.find((m) => m.user_id === id)?.display_name ?? null;
  // Framework ref: the card's, else the program manifest's `frameworkId`.
  const fw = card.framework_id ?? getProgramManifest(card.program)?.frameworkId ?? card.program;
  const readiness = useQuery({ queryKey: ['framework-readiness', slug, fw, card.certification_class], queryFn: () => getFrameworkReadiness(slug, fw, card.certification_class), retry: false });
  const blockers = useQuery({ queryKey: ['blockers', slug], queryFn: () => listBlockers(slug), staleTime: 30_000, retry: false });
  const gapsRef = useRef<HTMLHeadingElement>(null);
  const period = card.periods.find((p) => p.period_id === periodId) ?? null;
  const today = new Date().toISOString().slice(0, 10);
  const window = period?.window
    ? period.window.start > today ? `Assessment window opens ${fmtProgramDate(period.window.start)}` : `Assessment window ends ${fmtProgramDate(period.window.end)}`
    : null;
  const overdue = (blockers.data ?? []).filter((b) => b.overdue && b.programs.includes(card.profile_id));
  // R3-U14: a requirement with no mapped control is a gap too — the hero, the button and the list agree.
  // V109-1-19 (B4/B5): a KSI GAC Pro couldn't check is its own count and list, never a gap or partial.
  const reqs = readiness.data?.requirements ?? [];
  const couldnt = reqs.filter((r) => r.status !== 'met' && isCouldntRequirement(r));
  const gaps = reqs.filter((r) => (r.status === 'gap' || r.status === 'unmapped') && !couldnt.includes(r));
  const base = readiness.data ? readinessSplit(readiness.data.counts) : null;
  const ccPartial = couldnt.filter((r) => r.status === 'partial').length;
  const split = base ? { ...base, partial: Math.max(0, base.partial - ccPartial), gaps: Math.max(0, base.gaps - (couldnt.length - ccPartial)), couldnt: couldnt.length } : null;
  const ccRef = useRef<HTMLHeadingElement>(null);
  const showCouldnt = () => {
    globalThis.setTimeout(() => {
      const el = document.querySelector<HTMLElement>('[data-part="fr-couldnt"] [data-part="couldnt-check-fix"]') ?? ccRef.current;
      el?.scrollIntoView?.({ block: 'center' });
      el?.focus();
    }, 0);
  };
  // The pace runs to the window opening (Prepare's deadline), else its end.
  const by = period?.window ? (period.window.start > today ? period.window.start : period.window.end) : null;
  const daysLeft = by ? Math.round((Date.parse(`${by.slice(0, 10)}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000) : null;
  const todayDay = today;
  const from = `program:${card.program}`;

  return (
    <div className="gp-tab-panel" data-part="fedramp-prepare">
      {/* V98-5 review T5-H1: the same readiness card as Operate (ReadinessHero). V109-5-5: with the roll-up heading
          the page it carries readiness and the one primary; only the window line stays here. */}
      {rollupShown ? (window ? <p className="gp-dim" data-part="window">{window}</p> : null) : readiness.isLoading ? <section className="gp-hero" aria-label="KSI readiness"><Spinner label="Loading KSI readiness" /></section>
        : readiness.error || !readiness.data ? <section className="gp-hero" aria-label="KSI readiness"><Alert variant="error"><p>KSI readiness could not load.</p><Button onClick={() => void readiness.refetch()}>Retry</Button></Alert></section>
        // V101-6-3: zero KSIs loaded is not "No KSI gaps" (it read as success while checks failed).
        : readiness.data.counts.total === 0 ? <section className="gp-hero" aria-labelledby="ksi-none-h">
          <h3 id="ksi-none-h" className="gp-sec-h">No KSIs are loaded yet</h3>
          <p><Link to={`${prefix}/queue`}>Review the KSIs in the content queue</Link></p>
        </section>
        : (
          <ReadinessHero id="ksi-h" label={`KSI readiness${card.certification_class ? ` · Class ${card.certification_class}` : ''}`} pct={readiness.data.readiness_pct}
            split={split!} noun={['KSI', 'KSIs']} by={by} daysLeft={daysLeft} onCouldnt={showCouldnt}
            action={gaps.length
              ? <Button variant="primary" onClick={() => { gapsRef.current?.scrollIntoView?.({ block: 'start' }); gapsRef.current?.focus(); }}>{`Fix ${gaps.length} gap${gaps.length === 1 ? '' : 's'}`}</Button>
              : <span className="gp-dim">No KSI gaps.</span>}>
            {window ? <p className="gp-dim" data-part="window">{window}</p> : null}
          </ReadinessHero>
        )}

      <section aria-labelledby="fr-overdue-h" className="gp-sec">
        <h3 id="fr-overdue-h" className="gp-sec-h">{`Overdue · ${overdue.length}`}</h3>
        {blockers.isLoading ? <Spinner label="Loading overdue items" />
          : overdue.length ? (
            <ul className="gp-owed-list" aria-label="Overdue in this program">
              {overdue.map((b) => {
                const chip = dueChip(b, todayDay);
                const act = b.ref ? rowAct({ action: b.next_action as never, ref: b.ref, act: undefined, subtype: undefined }, prefix, from) : null;
                return (
                  <li key={b.item_key} className="gp-ow">
                    <span className="gp-ow-main">
                      {b.ref ? <Link className="gp-ow-t" to={itemHref(prefix, b.ref, from)}>{b.title}</Link> : <span className="gp-ow-t">{b.title}</span>}
                      <span className="gp-ow-meta"><Chip text={chip.text} tone={chip.tone} /><span>{b.holders.join(', ') || 'Unassigned'}</span></span>
                    </span>
                    {act && act.kind === 'link' ? <span className="gp-ow-act"><Link className="gp-linkbtn gp-linkbtn--compact" to={act.to} aria-label={`${act.label} · ${b.title}`}>{act.label}</Link></span> : null}
                  </li>
                );
              })}
            </ul>
          ) : <p className="gp-dim">Nothing overdue in this program.</p>}
      </section>

      {/* Built-UI #1 / #2: under the roll-up the filtered list (Not met / Couldn't check / Not yet tested / Met / N/A) replaces these two. */}
      {couldnt.length && !rollupShown ? (
        <section aria-labelledby="fr-cc-h" className="gp-sec" data-part="fr-couldnt">
          <h3 id="fr-cc-h" className="gp-sec-h" ref={ccRef} tabIndex={-1}>{`Couldn't check · ${couldnt.length}`}</h3>
          {/* V109-1-19 R3 (Medium): accept the risk once for the list (owner / admin), never per row. */}
          {admin ? <AcceptRisk slug={slug} viewerId={viewerId} targets={acceptTargets(couldnt)}
            onDone={() => void qc.invalidateQueries({ queryKey: ['framework-readiness', slug, fw] })} /> : null}
          <ul className="gp-owed-list" aria-label="KSIs GAC Pro couldn't check">
            {couldnt.map((r) => <KsiCouldntRow key={r.requirement_id} r={r} prefix={prefix} ownerName={ownerName} />)}
          </ul>
        </section>
      ) : null}

      {gaps.length && !rollupShown ? (
        <section aria-labelledby="fr-gaps-h" className="gp-sec">
          <h3 id="fr-gaps-h" className="gp-sec-h" ref={gapsRef} tabIndex={-1}>{`KSI gaps · ${gaps.length}`}</h3>
          <ul className="gp-owed-list" aria-label="KSI gaps">
            {gaps.map((r) => {
              const failing = r.crediting_controls.find((c) => c.state === 'fail' || c.state === 'stale' || c.state === 'no_evidence') ?? r.crediting_controls[0];
              const to = failing ? `${prefix}/findings?control=${encodeURIComponent(failing.control_id)}&requirement=${encodeURIComponent(r.ref)}` : `${prefix}/evidence`;
              return (
                <li key={r.requirement_id} className="gp-ow">
                  <span className="gp-ow-main"><span className="gp-ow-t">{`${r.ref} · ${r.title}`}</span>
                    {r.status === 'unmapped' ? <span className="gp-ow-meta">No control mapped yet</span> : r.reason ? <span className="gp-ow-meta">{r.reason}</span> : null}</span>
                  <span className="gp-ow-act"><Link className="gp-linkbtn gp-linkbtn--compact" to={to} aria-label={`Fix · ${r.ref} ${r.title}`}>Fix</Link></span>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
